import { useNavigate } from 'react-router-dom';
import { TSalesDataUploadResp } from 'pages/SalesData/types';
import { Box, Flex, Heading, Text, Button, Icon, useToast, Textarea } from '@chakra-ui/react';
import PageHeader from 'layout/pageHeader';
import MDropZone from 'components/molecules/MDropZone';
import { useState } from 'react';
import { GENERIC_EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { useTranslation } from 'helpers/languages/translations';
import { useUploadSalesDataMutation } from 'core/API/SalesDataApi';

const SalesDataUpload = () => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadSalesData, { isLoading }] = useUploadSalesDataMutation({
        fixedCacheKey: 'sales-data-upload-key',
    });
    const [comment, setComment] = useState('');

    const navigate = useNavigate();
    const translate = useTranslation();
    const goBack = () => {
        navigate(-1);
    };
    const toast = useToast();
    const handleChangeFile = (file: File | null) => {
        if (GENERIC_EXCEL_FILE_TYPE.includes(file?.type as string)) {
            setUploadedFile(file);
        } else {
            if (file !== null)
                toast({
                    title: translate('error.upload.file-invalid-type.xlsx'),
                    status: 'error',
                    isClosable: true,
                });
            setUploadedFile(null);
        }
    };

    const handleUploadSalesData = async () => {
        try {
            const bodyFormData = new FormData();
            bodyFormData.append('excelFile', uploadedFile as File);
            if (comment) bodyFormData.append('comment', comment);
            const res = (await uploadSalesData(bodyFormData)) as TSalesDataUploadResp;
            if (res.error) {
                if (res.error?.data.code === 'LIMIT_FILE_SIZE') throw translate('error.upload.file-size-limit');
                else throw translate('error.generic');
            } else {
                goBack();
                toast({
                    title: translate('sales_data.uplaod.message.success'),
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: error as string,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleUploadChecker = async () => {
        await handleUploadSalesData();
    };

    const renderDescriptionSection = () => {
        const UploadIcon = icons.upload;

        return (
            <Flex w="40%" justifyContent="flex-start" direction="column" textAlign="start">
                <Text fontSize="md" pt={4} mb={2.5}>
                    {translate('sales_data.upload.comment')}
                </Text>
                <Textarea
                    placeholder={translate('sales_data.upload.comment.placeholder') as string}
                    resize="none"
                    background="white"
                    fontSize="md"
                    value={comment}
                    onChange={(e) => {
                        setComment(e.target.value);
                    }}
                />
                <Text fontSize="md" pt={4} pb={3}>
                    {translate('sales_data.upload.file')}
                </Text>

                <Box py={4} pt={0}>
                    <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                </Box>
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        isLoading={isLoading}
                        onClick={intentionalyFloatingPromiseReturn(handleUploadChecker)}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('sales_data.upload.apply')}
                    </Button>
                </Flex>
            </Flex>
        );
    };

    return (
        <Box>
            <PageHeader
                pageTitle={translate('sales_data.upload.title') as string}
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
            />
            <Box px="32px">
                <Flex justifyContent="start">
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb="18px">
                        {translate('sales_data.upload.header')}
                    </Heading>
                </Flex>
                <Flex fontSize="sm" direction="row">
                    {renderDescriptionSection()}
                </Flex>
            </Box>
        </Box>
    );
};

export default SalesDataUpload;
